import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Info, Settings, ChevronUp, ChevronDown, Edit, Plus, X } from 'lucide-react';
import { api } from '@/api';

export function ContractRulesEditor({ contractType, initialRules, onChange }) {
  const [showAdvancedRules, setShowAdvancedRules] = useState(false);
  const [showClausePanel, setShowClausePanel] = useState(false);
  const [customRules, setCustomRules] = useState(initialRules || null);
  const [contractTypes, setContractTypes] = useState([]);
  const [allClauses, setAllClauses] = useState([]);
  const [expandedClauses, setExpandedClauses] = useState({});
  const [editingSubClause, setEditingSubClause] = useState(null);
  const [editText, setEditText] = useState('');
  const [addingToClause, setAddingToClause] = useState(null);
  const [newSubClauseNum, setNewSubClauseNum] = useState('');
  const [newSubClauseText, setNewSubClauseText] = useState('');

  const [clauseError, setClauseError] = useState(false);

  useEffect(() => {
    api.get('/contracts/types').then(r => setContractTypes(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (contractType) {
      setClauseError(false);
      // Try exact match first, then NEC3 fallback for "NEC"
      const tryType = contractType === 'NEC' ? 'NEC3' : contractType;
      api.get(`/contracts/${tryType}/all-clauses`)
        .then(r => setAllClauses(r.data || []))
        .catch(() => {
          setAllClauses([]);
          setClauseError(true);
        });
    }
  }, [contractType]);

  useEffect(() => { setCustomRules(initialRules || null); }, [initialRules]);

  const getContractDefaults = (ct) => {
    const found = contractTypes.find(c => c.value === ct);
    return found?.defaults || {};
  };

  const getCustomClauses = () => (customRules || {}).custom_clauses || {};

  const emitChange = (updated) => { setCustomRules(updated); onChange(updated); };

  const handleCustomPeriodChange = (field, subField, value) => {
    const defaults = getContractDefaults(contractType);
    const current = customRules || defaults || {};
    const existing = current[field] || {};
    emitChange({ ...current, [field]: { ...existing, [subField]: subField === 'value' ? parseInt(value) || '' : value } });
  };

  const handleCustomRuleChange = (field, value) => {
    const defaults = getContractDefaults(contractType);
    const current = customRules || defaults || {};
    emitChange({ ...current, [field]: value });
  };

  const handleClauseAmend = (clauseNum, subNum, newText) => {
    const cc = getCustomClauses();
    const clauseData = cc[clauseNum] || { amended_sub_clauses: {}, added_sub_clauses: {} };
    const newCC = { ...cc, [clauseNum]: { ...clauseData, amended_sub_clauses: { ...clauseData.amended_sub_clauses, [subNum]: newText } } };
    const defaults = getContractDefaults(contractType);
    const current = customRules || defaults || {};
    emitChange({ ...current, custom_clauses: newCC });
    setEditingSubClause(null);
    setEditText('');
  };

  const handleAddSubClause = (clauseNum) => {
    if (!newSubClauseNum || !newSubClauseText) return;
    const cc = getCustomClauses();
    const clauseData = cc[clauseNum] || { amended_sub_clauses: {}, added_sub_clauses: {} };
    const newCC = { ...cc, [clauseNum]: { ...clauseData, added_sub_clauses: { ...clauseData.added_sub_clauses, [newSubClauseNum]: newSubClauseText } } };
    const defaults = getContractDefaults(contractType);
    const current = customRules || defaults || {};
    emitChange({ ...current, custom_clauses: newCC });
    setAddingToClause(null);
    setNewSubClauseNum('');
    setNewSubClauseText('');
  };

  const ct = contractTypes.find(c => c.value === contractType);
  const dmName = ct?.terminology?.decision_maker || 'Decision Maker';
  const defaults = getContractDefaults(contractType);
  const vals = customRules || defaults || {};

  return (
    <div className="space-y-3">
      {/* Period Amendments */}
      <div className="border border-slate-200 rounded-sm">
        <button type="button" onClick={() => setShowAdvancedRules(!showAdvancedRules)}
          className="w-full flex items-center justify-between px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-600 hover:bg-slate-50 transition-colors"
          data-testid="toggle-advanced-rules">
          <span className="flex items-center gap-2"><Settings className="h-3.5 w-3.5" />Contract Amendments</span>
          {showAdvancedRules ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
        {showAdvancedRules && (
          <div className="px-4 pb-4 space-y-4 border-t border-slate-100" data-testid="advanced-rules-panel">
            <p className="text-xs text-slate-500 mt-3 flex items-center gap-1">
              <Info className="h-3 w-3 shrink-0" />Amend default contract periods and consequences.
            </p>
            {/* Notice Period */}
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1 block">Notice Period</Label>
              <div className="flex gap-2">
                <Input type="number" min="1" value={vals.notice_period?.value || ''} onChange={(e) => handleCustomPeriodChange('notice_period', 'value', e.target.value)} className="rounded-sm w-24" placeholder="Days" />
                <select value={vals.notice_period?.unit || 'calendar_days'} onChange={(e) => handleCustomPeriodChange('notice_period', 'unit', e.target.value)} className="flex h-10 rounded-sm border border-input bg-background px-2 py-2 text-xs">
                  <option value="working_days">Working Days</option><option value="calendar_days">Calendar Days</option><option value="weeks">Weeks</option>
                </select>
              </div>
            </div>
            {/* Claim Period */}
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1 block">{ct?.terminology?.claim || 'Claim'} Period</Label>
              <div className="flex gap-2">
                <Input type="number" min="1" value={vals.claim_period?.value || vals.detailed_claim_period?.value || vals.quotation_period?.value || ''} onChange={(e) => {
                  const pk = contractType === 'FIDIC 1999' ? 'detailed_claim_period' : contractType?.startsWith('NEC') ? 'quotation_period' : 'claim_period';
                  handleCustomPeriodChange(pk, 'value', e.target.value);
                }} className="rounded-sm w-24" placeholder="Days" />
                <select value={vals.claim_period?.unit || vals.detailed_claim_period?.unit || vals.quotation_period?.unit || 'calendar_days'} onChange={(e) => {
                  const pk = contractType === 'FIDIC 1999' ? 'detailed_claim_period' : contractType?.startsWith('NEC') ? 'quotation_period' : 'claim_period';
                  handleCustomPeriodChange(pk, 'unit', e.target.value);
                }} className="flex h-10 rounded-sm border border-input bg-background px-2 py-2 text-xs">
                  <option value="working_days">Working Days</option><option value="calendar_days">Calendar Days</option><option value="weeks">Weeks</option>
                </select>
              </div>
            </div>
            {/* Response Period */}
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1 block">{dmName} Response Period</Label>
              <div className="flex gap-2">
                <Input type="number" min="1" value={vals.determination_period?.value || vals.engineer_response_period?.value || vals.pm_assessment_period?.value || ''} onChange={(e) => {
                  const pk = contractType === 'JBCC' ? 'determination_period' : contractType === 'FIDIC 1999' ? 'engineer_response_period' : 'pm_assessment_period';
                  handleCustomPeriodChange(pk, 'value', e.target.value);
                }} className="rounded-sm w-24" placeholder="Days" />
                <select value={vals.determination_period?.unit || vals.engineer_response_period?.unit || vals.pm_assessment_period?.unit || 'calendar_days'} onChange={(e) => {
                  const pk = contractType === 'JBCC' ? 'determination_period' : contractType === 'FIDIC 1999' ? 'engineer_response_period' : 'pm_assessment_period';
                  handleCustomPeriodChange(pk, 'unit', e.target.value);
                }} className="flex h-10 rounded-sm border border-input bg-background px-2 py-2 text-xs">
                  <option value="working_days">Working Days</option><option value="calendar_days">Calendar Days</option><option value="weeks">Weeks</option>
                </select>
              </div>
            </div>
            {/* Consequence */}
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1 block">If {dmName} Fails to Respond</Label>
              <select value={vals.determination_consequence || ''} onChange={(e) => handleCustomRuleChange('determination_consequence', e.target.value)} className="flex h-10 w-full rounded-sm border border-input bg-background px-3 py-2 text-xs">
                <option value="deemed_rejected">Deemed Rejected</option><option value="deemed_accepted">Deemed Accepted</option><option value="referable_to_dab">Referable to DAB / Adjudicator</option>
              </select>
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1 block">Clause Reference</Label>
              <Input value={vals.determination_clause || ''} onChange={(e) => handleCustomRuleChange('determination_clause', e.target.value)} className="rounded-sm text-xs" placeholder="e.g. 23.6 (amended)" />
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1 block">Consequence Text (as per amendment)</Label>
              <textarea value={vals.determination_consequence_text || ''} onChange={(e) => handleCustomRuleChange('determination_consequence_text', e.target.value)} className="flex w-full rounded-sm border border-input bg-background px-3 py-2 text-xs min-h-[60px] ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" placeholder="Describe the amended consequence..." />
            </div>
          </div>
        )}
      </div>

      {/* Clauses Panel */}
      <div className="border border-slate-200 rounded-sm">
        <button type="button" onClick={() => setShowClausePanel(!showClausePanel)}
          className="w-full flex items-center justify-between px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-600 hover:bg-slate-50 transition-colors"
          data-testid="toggle-clause-panel">
          <span className="flex items-center gap-2">
            <Settings className="h-3.5 w-3.5" />Clauses & Sub-Clauses
            {Object.keys(getCustomClauses()).length > 0 && <span className="bg-amber-100 text-amber-700 text-[9px] font-bold px-1.5 py-0.5 rounded">{Object.keys(getCustomClauses()).length} amended</span>}
          </span>
          {showClausePanel ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
        {showClausePanel && (
          <div className="px-4 pb-4 border-t border-slate-100" data-testid="clause-panel">
            <p className="text-xs text-slate-500 mt-3 mb-3 flex items-center gap-1">
              <Info className="h-3 w-3 shrink-0" />View, amend, or add sub-clauses for this contract type.
            </p>
            {allClauses.length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-4">
                {clauseError ? 'No clauses available for this contract type.' : 'Loading clauses...'}
              </p>
            ) : (
              <div className="space-y-1 max-h-[340px] overflow-y-auto pr-1">
                {allClauses.map((clause) => {
                  const isExpanded = expandedClauses[clause.clause_number];
                  const cc = getCustomClauses();
                  const hasAmendments = !!(cc[clause.clause_number]?.amended_sub_clauses && Object.keys(cc[clause.clause_number].amended_sub_clauses).length > 0);
                  const hasAdditions = !!(cc[clause.clause_number]?.added_sub_clauses && Object.keys(cc[clause.clause_number].added_sub_clauses).length > 0);
                  const isModified = hasAmendments || hasAdditions;
                  const effectiveSubClauses = [...(clause.sub_clauses || [])];
                  const addedSubs = cc[clause.clause_number]?.added_sub_clauses || {};
                  Object.entries(addedSubs).forEach(([num, text]) => {
                    if (!effectiveSubClauses.find(s => s.sub_clause_number === num)) {
                      effectiveSubClauses.push({ sub_clause_number: num, text, isCustom: true });
                    }
                  });
                  effectiveSubClauses.sort((a, b) => a.sub_clause_number.localeCompare(b.sub_clause_number, undefined, { numeric: true }));

                  return (
                    <div key={clause.clause_number} className={`border rounded-sm ${isModified ? 'border-amber-200 bg-amber-50/30' : 'border-slate-100'}`}>
                      <button type="button" onClick={() => setExpandedClauses(p => ({ ...p, [clause.clause_number]: !p[clause.clause_number] }))}
                        className="w-full flex items-center justify-between px-3 py-2 text-xs hover:bg-slate-50/50 transition-colors">
                        <span className={`font-medium ${isModified ? 'text-amber-700' : 'text-slate-700'}`}>
                          Clause {clause.clause_number}: {clause.title}
                        </span>
                        {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                      </button>
                      {isExpanded && (
                        <div className="px-3 pb-3 space-y-1.5 border-t border-slate-100">
                          {effectiveSubClauses.map((sub) => {
                            const amended = cc[clause.clause_number]?.amended_sub_clauses?.[sub.sub_clause_number];
                            const isEditing = editingSubClause === `${clause.clause_number}-${sub.sub_clause_number}`;
                            return (
                              <div key={sub.sub_clause_number} className={`flex items-start gap-2 px-2 py-1.5 rounded ${amended ? 'bg-amber-50 border border-amber-200' : sub.isCustom ? 'bg-green-50 border border-green-200' : ''}`}>
                                <span className="text-[10px] font-mono text-slate-400 mt-0.5 shrink-0">{sub.sub_clause_number}</span>
                                {isEditing ? (
                                  <div className="flex-1 space-y-1">
                                    <textarea value={editText} onChange={e => setEditText(e.target.value)} className="w-full text-xs border rounded-sm px-2 py-1 min-h-[40px]" />
                                    <div className="flex gap-1">
                                      <button type="button" onClick={() => handleClauseAmend(clause.clause_number, sub.sub_clause_number, editText)} className="text-[10px] bg-blue-600 text-white px-2 py-0.5 rounded">Save</button>
                                      <button type="button" onClick={() => setEditingSubClause(null)} className="text-[10px] text-slate-500 px-2 py-0.5">Cancel</button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex-1 flex items-start justify-between gap-1">
                                    <p className="text-[11px] text-slate-600 leading-relaxed">{amended || sub.text}</p>
                                    <button type="button" onClick={() => { setEditingSubClause(`${clause.clause_number}-${sub.sub_clause_number}`); setEditText(amended || sub.text); }}
                                      className="shrink-0 text-slate-400 hover:text-blue-600 p-0.5"><Edit className="h-3 w-3" /></button>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          {addingToClause === clause.clause_number ? (
                            <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-sm space-y-1">
                              <Input value={newSubClauseNum} onChange={e => setNewSubClauseNum(e.target.value)} placeholder="e.g. 23.6.1" className="text-xs h-7" />
                              <textarea value={newSubClauseText} onChange={e => setNewSubClauseText(e.target.value)} className="w-full text-xs border rounded-sm px-2 py-1 min-h-[40px]" placeholder="Sub-clause text..." />
                              <div className="flex gap-1">
                                <button type="button" onClick={() => handleAddSubClause(clause.clause_number)} className="text-[10px] bg-green-600 text-white px-2 py-0.5 rounded">Add</button>
                                <button type="button" onClick={() => setAddingToClause(null)} className="text-[10px] text-slate-500 px-2 py-0.5">Cancel</button>
                              </div>
                            </div>
                          ) : (
                            <button type="button" onClick={() => setAddingToClause(clause.clause_number)} className="mt-1 text-[10px] text-green-600 hover:text-green-700 flex items-center gap-1">
                              <Plus className="h-3 w-3" />Add sub-clause
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ContractRulesEditor;
